import { BadgeInfoComponent } from '@components/shared/badge-info/badge-info.component';
import { EmptyStateComponent } from '@components/shared/empty-state/empty-state.component';
import { PaginationComponent } from '@components/shared/pagination/pagination.component';
import { PaginationEvent } from '@models/pagination.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LogService } from '@services/util/log.service';
import { FormatDatePipe } from 'app/pipes/format-date.pipe';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, finalize, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HostActivityLogService } from '../../shared/host-activity-log.service';
import { invoiceDeleteMetadata } from '../../shared/host-activity.metadata';
import { InvoiceFilter } from '../models/invoice-filter.model';
import { InvoiceRow, PaginatedInvoices } from '../models/invoice-listing.model';
import { InvoiceListingService } from '../services/invoice-listing.service';
import { InvoiceFiltersComponent } from './invoice-filters.component';

interface SortEvent {
  field: string;
  order: number;
  data?: InvoiceRow[];
}

@Component({
  selector: 'app-invoice-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    TagModule,
    MessageModule,
    PaginationComponent,
    EmptyStateComponent,
    BadgeInfoComponent,
    InvoiceFiltersComponent,
    FormatDatePipe,
    ConfirmDialogModule,
    ToastModule,
    TooltipModule
  ],
  providers: [ConfirmationService, MessageService],
  template: `
    <div>
      <!-- Toast for success/error messages -->
      <p-toast></p-toast>

      <!-- Confirmation Dialog for Delete Operations -->
      <p-confirmDialog></p-confirmDialog>

      <!-- Filter Panel -->
      <app-invoice-filters
        [activeFilter]="activeFilter"
        (filterChange)="onFilterChange($event)"
      >
      </app-invoice-filters>

      <!-- Loading Indicator -->
      @if (loading) {
        <div class="my-6 flex flex-col items-center justify-center">
          <div class="relative h-14 w-14">
            <div class="absolute h-14 w-14 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
          </div>
          <span class="mt-3 font-medium">{{ 'common.loading' | translate }}</span>
        </div>
      }

      <!-- Error state -->
      @if (error$ | async; as errorMessage) {
        @if (errorMessage) {
          <div class="surface-danger border-round mb-3 p-4 text-white">
            {{ errorMessage }}
          </div>
        }
      }

      <!-- Empty state when no invoices and not loading -->
      @if (!loading && (!paginatedInvoices || !paginatedInvoices.items.length)) {
        <div class="card">
          <div class="p-5 text-center">
            <app-empty-state
              [title]="'invoices.noInvoices'"
              [description]="'invoices.noInvoicesDescription'"
            >
            </app-empty-state>
          </div>
        </div>
      }

      <!-- Table with invoices (only rendered when data is available) -->
      @if (!loading && paginatedInvoices && paginatedInvoices.items.length > 0) {
        <div class="">
          <p-table
            #dt
            [value]="paginatedInvoices.items"
            [stripedRows]="true"
            styleClass="p-datatable-sm"
            [rowHover]="true"
            [tableStyle]="{ 'min-width': '80rem' }"
            responsiveLayout="scroll"
            (sortFunction)="onSort($event)"
            [customSort]="true"
            dataKey="id"
          >
            <ng-template pTemplate="header">
              <tr>
                <th style="vertical-align: middle">
                  {{ 'invoices.invoiceNumber' | translate }}
                </th>
                <th
                  [pSortableColumn]="'customerName'"
                  style="vertical-align: middle"
                >
                  <div class="flex items-center justify-between">
                    {{ 'invoices.customer' | translate }}
                    <span class="ml-1">
                      @if (sortState.field === 'customerName' && sortState.direction === 'asc') {
                        <i class="pi pi-sort-up"></i>
                      }
                      @if (sortState.field === 'customerName' && sortState.direction === 'desc') {
                        <i class="pi pi-sort-down"></i>
                      }
                      @if (sortState.field !== 'customerName') {
                        <i class="pi pi-sort text-300"></i>
                      }
                    </span>
                  </div>
                </th>
                <th
                  [pSortableColumn]="'invoiceStatus'"
                  style="vertical-align: middle"
                >
                  <div class="flex items-center justify-between">
                    {{ 'invoices.status' | translate }}
                    <span class="ml-1">
                      @if (sortState.field === 'invoiceStatus' && sortState.direction === 'asc') {
                        <i class="pi pi-sort-up"></i>
                      }
                      @if (sortState.field === 'invoiceStatus' && sortState.direction === 'desc') {
                        <i class="pi pi-sort-down"></i>
                      }
                      @if (sortState.field !== 'invoiceStatus') {
                        <i class="pi pi-sort text-300"></i>
                      }
                    </span>
                  </div>
                </th>
                <th style="vertical-align: middle">
                  {{ 'invoices.notes' | translate }}
                </th>
                <th style="vertical-align: middle">
                  {{ 'invoices.fulfillmentDate' | translate }}
                </th>
                <th style="vertical-align: middle">
                  {{ 'invoices.amount' | translate }}
                </th>
                <th style="vertical-align: middle">
                  {{ 'invoices.paid' | translate }}
                </th>
                <th style="vertical-align: middle">
                  {{ 'invoices.source' | translate }}
                </th>
                <th style="vertical-align: middle; width: 180px">
                  {{ 'common.actions' | translate }}
                </th>
              </tr>
            </ng-template>
            <ng-template
              pTemplate="body"
              let-invoice
              let-rowIndex="rowIndex"
            >
              <tr>
                <td>
                  <span class="font-medium">{{ invoice.invoiceNumber || 'N/A' }}</span>
                </td>
                <td>
                  <span>{{ invoice.customerName || ('invoices.unknownCustomer' | translate) }}</span>
                </td>
                <td>
                  <app-badge-info [badge]="invoice.invoiceStatus"></app-badge-info>
                </td>
                <td>
                  <span
                    [title]="invoice.userNote"
                    class="block max-w-32 truncate"
                  >
                    {{ invoice.userNote || '-' }}
                  </span>
                </td>
                <td>
                  @if (invoice.fulfillmentDate) {
                    <span>{{ invoice.fulfillmentDate | formatDate }}</span>
                  } @else {
                    <span class="text-surface-500">-</span>
                  }
                </td>
                <td>
                  <div class="flex items-center gap-1">
                    <span class="font-medium">{{ invoice.grossTotal | number: '1.2-2' }}</span>
                    <span class="text-sm text-surface-600 dark:text-surface-400 ml-1">{{ invoice.currency.code }}</span>
                  </div>
                </td>
                <td>
                  @if (invoice.paidStatus) {
                    <p-tag
                      [value]="'invoices.paid' | translate"
                      severity="success"
                      icon="pi pi-check"
                    ></p-tag>
                  } @else {
                    <p-tag
                      [value]="'invoices.unpaid' | translate"
                      severity="danger"
                      icon="pi pi-times"
                    ></p-tag>
                  }
                </td>
                <td>
                  @if (invoice.bookingId) {
                    <button
                      pButton
                      type="button"
                      [label]="invoice.invoiceSource"
                      class="p-button-link p-button-sm p-0 text-left"
                      (click)="navigateToBooking(invoice.bookingId, $event)"
                    ></button>
                  } @else {
                    <span class="text-sm">{{ invoice.invoiceSource }}</span>
                  }
                </td>
                <td>
                  <div class="flex gap-1">
                    <!-- Download PDF Button - Hidden until backend is ready -->
                    <!-- TODO: Uncomment when backend PDF endpoint is implemented
                    <button
                      pButton
                      type="button"
                      icon="pi pi-download"
                      class="p-button-text p-button-sm"
                      [pTooltip]="'invoices.actions.downloadPdf' | translate"
                      (click)="downloadPdf(invoice.id)"
                      [disabled]="!invoice.invoiceNumber"
                    ></button>
                    -->

                    <!-- View Button -->
                    <button
                      pButton
                      type="button"
                      icon="pi pi-eye"
                      class="p-button-text p-button"
                      [pTooltip]="'common.view' | translate"
                      (click)="viewInvoice(invoice.id)"
                    ></button>

                    <!-- Edit Button -->
                    <button
                      pButton
                      type="button"
                      icon="pi pi-pencil"
                      class="p-button-text p-button"
                      [pTooltip]="'common.edit' | translate"
                      (click)="editInvoice(invoice.id)"
                    ></button>

                    <!-- Delete Button -->
                    <button
                      pButton
                      type="button"
                      icon="pi pi-trash"
                      class="p-button-text p-button p-button-danger"
                      [pTooltip]="'common.delete' | translate"
                      (click)="confirmDelete(invoice)"
                    ></button>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td
                  colspan="9"
                  class="text-center"
                >
                  {{ 'invoices.noInvoicesFound' | translate }}
                </td>
              </tr>
            </ng-template>
          </p-table>

          <!-- Pagination controls -->
          <app-pagination
            [currentPage]="currentPage"
            [totalItems]="paginatedInvoices.totalCount"
            [pageSize]="pageSize"
            (pageChange)="onPageChange($event)"
          ></app-pagination>
        </div>
      }
    </div>
  `
})
export class InvoiceTableComponent implements OnInit, OnDestroy {
  @Input() filter: InvoiceFilter | undefined;
  @Output() viewInvoiceDetails = new EventEmitter<number>();
  @Output() editInvoiceDetails = new EventEmitter<number>();
  @Output() downloadInvoicePdf = new EventEmitter<number>();

  private readonly invoiceListingService = inject(InvoiceListingService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly log = inject(LogService);
  private readonly hostActivityLog = inject(HostActivityLogService);
  private readonly destroy$ = new Subject<void>();

  pageSize = 30; // API fixed page size
  currentPage = 1;
  paginatedInvoices: PaginatedInvoices | null = null;
  loading = false;

  // Sort state for backend sorting
  sortField: string | null = 'invoiceDate';
  sortOrder: 'asc' | 'desc' | null = 'desc';

  // UI sort state for display
  sortState: { field: string | null; direction: 'asc' | 'desc' | null } = {
    field: 'invoiceDate',
    direction: 'desc'
  };

  // Field mapping between UI and API
  private readonly fieldMapping: Record<string, string> = {
    invoiceNumber: 'invoice_number',
    customerName: 'customer_user.name',
    invoiceStatus: 'invoiceStatus.id',
    fulfillmentDate: 'fulfillment_date',
    grossTotal: 'gross_total',
    paidStatus: 'paid'
  };

  activeFilter?: InvoiceFilter;

  // Error state
  private readonly errorSubject = new BehaviorSubject<string | null>(null);
  readonly error$ = this.errorSubject.asObservable();

  ngOnInit(): void {
    this.restoreStateFromUrl();
    this.loadInvoices();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadInvoices(): void {
    this.loading = true;
    this.errorSubject.next(null);

    this.invoiceListingService
      .getInvoices(this.currentPage, this.pageSize, this.sortField, this.sortOrder, this.activeFilter)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          this.paginatedInvoices = response;
          this.log.debug('Loaded invoices:', response);
        },
        error: (error) => {
          this.log.error('Error loading invoices:', error);
          this.errorSubject.next(this.translateService.instant('invoices.loadError'));
          this.paginatedInvoices = null;
        }
      });
  }

  onFilterChange(filter: InvoiceFilter): void {
    this.activeFilter = filter;
    this.currentPage = 1;
    this.updateUrlParams();
    this.loadInvoices();
  }

  onPageChange(event: PaginationEvent): void {
    this.currentPage = event.page;
    this.updateUrlParams();
    this.loadInvoices();
  }

  onSort(event: SortEvent): void {
    const uiField = event.field;
    if (uiField === this.sortState.field) {
      if (this.sortState.direction === 'asc') {
        this.sortState.direction = 'desc';
      } else if (this.sortState.direction === 'desc') {
        this.sortState.field = null;
        this.sortState.direction = null;
      } else {
        this.sortState.direction = 'asc';
      }
    } else {
      this.sortState.field = uiField;
      this.sortState.direction = 'asc';
    }
    const apiField = this.sortState.field ? (this.fieldMapping[this.sortState.field] || this.sortState.field) : null;
    this.sortField = apiField;
    this.sortOrder = this.sortState.direction;
    this.currentPage = 1;
    this.updateUrlParams();
    this.loadInvoices();
  }

  viewInvoice(invoiceId: number): void {
    this.viewInvoiceDetails.emit(invoiceId);
  }

  editInvoice(invoiceId: number): void {
    this.editInvoiceDetails.emit(invoiceId);
  }

  downloadPdf(invoiceId: number): void {
    this.downloadInvoicePdf.emit(invoiceId);
  }

  navigateToBooking(bookingId: number, event?: MouseEvent): void {
    if (event?.ctrlKey || event?.metaKey) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/bookings', bookingId])
      );
      window.open(url, '_blank');
    } else {
      this.router.navigate(['/bookings', bookingId]);
    }
  }

  private restoreStateFromUrl(): void {
    const params = this.route.snapshot.queryParams;
    if (params['page']) {
      this.currentPage = parseInt(params['page'], 10) || 1;
    }
    if (params['sortField']) {
      this.sortField = params['sortField'];
      this.sortState.field = this.getUiFieldFromApiField(params['sortField']);
    }
    if (params['sortOrder']) {
      this.sortOrder = params['sortOrder'] as 'asc' | 'desc';
      this.sortState.direction = params['sortOrder'] as 'asc' | 'desc';
    }
    if (params['filter']) {
      try {
        this.activeFilter = JSON.parse(params['filter']);
      } catch (e) {
        this.log.error('Failed to parse filter from URL:', e);
      }
    }
  }

  private updateUrlParams(): void {
    const queryParams: any = {};
    if (this.currentPage > 1) {
      queryParams['page'] = this.currentPage;
    } else {
      queryParams['page'] = null;
    }
    // Null (not omit) on every param so queryParamsHandling: 'merge' removes stale values when sort/filter are cleared
    queryParams['sortField'] = this.sortField ?? null;
    queryParams['sortOrder'] = this.sortOrder ?? null;
    if (this.activeFilter && Object.keys(this.activeFilter).length > 0) {
      queryParams['filter'] = JSON.stringify(this.activeFilter);
    } else {
      queryParams['filter'] = null;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private getUiFieldFromApiField(apiField: string): string | null {
    const reverseMapping: Record<string, string> = {
      'invoice_number': 'invoiceNumber',
      'customerUser.name': 'customerName',
      'invoiceStatus.id': 'invoiceStatus',
      'fulfillment_date': 'fulfillmentDate',
      'gross_total': 'grossTotal',
      'paid': 'paidStatus'
    };
    return reverseMapping[apiField] || apiField;
  }

  confirmDelete(invoice: InvoiceRow): void {
    const invoiceNumber = invoice.invoiceNumber || 'N/A';
    this.confirmationService.confirm({
      message: this.translateService.instant('invoices.delete.confirmMessage', { invoiceNumber }),
      header: this.translateService.instant('common.delete.confirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('common.yesDelete'),
      rejectLabel: this.translateService.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteInvoice(invoice);
      }
    });
  }

  private deleteInvoice(invoice: InvoiceRow): void {
    const deleted = invoiceDeleteMetadata({ invoiceId: invoice.id, flats: invoice.bookingFlats, invoiceNumber: invoice.invoiceNumber });
    this.invoiceListingService
      .deleteInvoice(invoice.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (success) => {
          if (success) {
            this.hostActivityLog.log('invoice_delete', deleted);
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.success'),
              detail: this.translateService.instant('invoices.delete.success')
            });
            this.loadInvoices(); // Reload the table
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.error'),
              detail: this.translateService.instant('invoices.delete.error')
            });
          }
        },
        error: (error) => {
          this.log.error('Error deleting invoice:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant('invoices.delete.error')
          });
        }
      });
  }
}
